import { LocalStorageReactive } from '@adornis/base/rxjs-extension/index.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MatchAPI } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { getMenteeUserClass, getMentorUserClass } from '@adornis/bh-matching-user/user-class.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
// import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
// import '@adornis/buchungsheld-theme/components/tr-data-view.js';
// import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { LOADER } from '@adornis/chemistry/elements/components/x-loader.js';
// import '@adornis/chemistry/elements/components/x-section.js';
// import '@adornis/chemistry/elements/components/x-tabs';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/legacy/x-data-view.js';
import { BHFilterEditor } from '@adornis/buchungsheld-components/bh-filter-editor.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/popover/x-dropdown-selection.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs';
import { buildBuddyExportBuildOptions } from '../api/BuddyExportBuildOptions.js';
import {
  BUDDY_EXPORT_ENUM_FIELDS,
  BUDDY_EXPORT_ENUM_LIST_FIELDS,
  BuddyExportDataView,
} from '../db/BuddyExportDataView.js';
import { BuddyDialogPrepareExport } from './buddy-dialog-prepare-export.js';

@customElement('buddy-page-export')
export class BuddyPageExport extends ChemistryLitElement {
  private _defaultView = new View({ configuredColumns: [{ fieldName: 'email' }, { fieldName: '_class' }] as any });

  private _localStorage = new LocalStorageReactive<View<typeof BuddyUser>>('ExportView', this._defaultView);
  @state() private _curView = new RXController(
    this,
    this._localStorage.pipe(
      map(curView => (curView ? constructValue<View<typeof BuddyUser>>(curView) : this._defaultView)),
    ),
  );

  translator = new TranslationController<any>(this);
  currentTimeframe = new RXController(this, currentlySelectedTimeframe) as any;

  protected actionRow(dataOptions: DataViewOptions<typeof BuddyUser>): Renderable[] {
    return [
      html`
        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({ props: { dataOptions, timeframe, userClass: getMenteeUserClass() } });
          }}
        >
          ${this.translator.translate('buddy', 'exportMenteeUser')}
        </tr-button>

        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({ props: { dataOptions, timeframe, userClass: getMentorUserClass() } });
          }}
        >
          ${this.translator.translate('buddy', 'exportMentorUser')}
        </tr-button>

        <!-- export matches -->
        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            const menteeUserClass = getMenteeUserClass();
            const mentorUserClass = getMentorUserClass();

            const { dataViewOptions } = buildFullOptions({
              class: Match,
              operation: (skip, limit, filter) =>
                MatchAPI.subscribePaginatedAndFiltered(skip, limit, filter, timeframe?._id),
              customColumns: [
                { fieldName: '_id' },
                { fieldName: 'createdAt' },
                ...Object.values(menteeUserClass.fields)
                  .filter(field => !userFieldBlackList.includes(field.name))
                  .map(field => ({ fieldName: 'mentee.' + field.name })),
                ...Object.values(mentorUserClass.fields)
                  .filter(field => !userFieldBlackList.includes(field.name))
                  .map(field => ({ fieldName: 'mentor.' + field.name })),
              ],
            });

            void XDialog.waitFor(exportDataView(dataViewOptions, { exportClass: BuddyExportDataView }));
          }}
        >
          ${this.translator.translate('buddy', 'exportAllMatches')}
        </tr-button>

        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({
              props: { dataOptions, timeframe, onlyWithMatch: true, userClass: getMenteeUserClass() },
            });
          }}
        >
          ${this.translator.translate('buddy', 'exportMatchedMenteeUser')}
        </tr-button>

        <tr-button
          icon="download"
          @click=${() => {
            const timeframe = this.currentTimeframe.value;

            BuddyDialogPrepareExport.showPopup({
              props: { dataOptions, timeframe, onlyWithMatch: true, userClass: getMentorUserClass() },
            });
          }}
        >
          ${this.translator.translate('buddy', 'exportMatchedMentorUser')}
        </tr-button>
      `,
      html` <slot name="additional-buttons"></slot> `,
    ];
  }

  override render() {
    const currentTimeframe = this.currentTimeframe;
    if (currentTimeframe.isLoading) return LOADER;
    if (!currentTimeframe.value)
      return html` <x-text> ${this.translator.translate('buddy', 'noTimeFrameChoosen')} </x-text> `;

    const dataOptions: DataViewOptions<typeof BuddyUser> = {
      class: BuddyUser,
      view: this._curView.value ?? this._defaultView,
      customColumns: [
        { fieldName: 'email' },
        {
          fieldName: '_class',
          filterAspect: {
            ...StringAspectConfig('equals'),
            renderEditor: () => html`
              <x-dropdown-selection
                .selectables=${[BuddyUserMentee._class, BuddyUserMentor._class]}
              ></x-dropdown-selection>
            `,
          } as any,
        },
        ...BUDDY_EXPORT_ENUM_LIST_FIELDS.map(fieldName => ({
          fieldName,
          renderItem: userEntity => {
            return userEntity[fieldName]
              ? html`
                  <x-flex space="xxs">
                    ${(userEntity[fieldName] ?? []).map(
                      curEnum => html` <x-text>${this.translator.translate('enums', curEnum)},</x-text> `,
                    )}
                  </x-flex>
                `
              : nothing;
          },
        })),
        ...BUDDY_EXPORT_ENUM_FIELDS.map(fieldName => ({
          fieldName,
          renderItem: userEntity =>
            userEntity[fieldName]
              ? html` <x-text>${this.translator.translate('enums', userEntity[fieldName])}</x-text> `
              : nothing,
        })),
      ],
      onViewChanged: async nextView => this._localStorage.next(nextView),
      // TODO: put that in matching package directly (have to add translations in all other entrypoints)
      defaultFieldMapping: {
        ['string']: value => {
          if (value === getMenteeUserClass()._class) return clientTranslator.translate('user', value);
          if (value === getMentorUserClass()._class) return clientTranslator.translate('user', value);
          return value;
        },
      },
    };

    const opts = buildBuddyExportBuildOptions(dataOptions, currentTimeframe.value);

    return html`
      <tr-flex space="lg">
        <bh-breadcrumb .goUp=${() => goTo('/')}></bh-breadcrumb>

        <tr-flex space="md">
          <x-h1> ${this.translator.translate('route', 'export')} </x-h1>
          <tr-flex space="md">
            <tr-flex horizontal space="sm"> ${this.actionRow(dataOptions)} </tr-flex>

            ${BHFilterEditor.render({ options: opts.filterOptions })}
            <tr-data-view .options=${opts.dataViewOptions}></tr-data-view>
          </tr-flex>
        </tr-flex>
      </tr-flex>
    `;
  }
}
